import { FC, useCallback, useMemo, useState, useEffect, useRef } from 'react'
import ScrollLock, { TouchScrollable } from 'react-scrolllock'

import ActionButton, { ActionButtonProps } from '../ActionButton'
import NavPush from '../NavPush'
import { NavMenuProps } from '../NavMenu'
import { TextLinkProps } from '../A'
import { HeaderSearchProps } from '../HeaderSearch'

import * as SC from './styled'

export type NavMobileMenuProps = NavMenuProps & {
  isOpen: boolean
  toggleMobileMenuOpen?: () => void
  search: HeaderSearchProps
  linkAccount?: TextLinkProps
  linkHome?: TextLinkProps
  socials?: ActionButtonProps[]
  textClose?: string
  textBack?: string
  mobileSearchText: string
}

const NavMobileMenu: FC<NavMobileMenuProps> = (props) => {
  const {
    className,
    links,
    toggleMobileMenuOpen,
    isOpen,
    linkAccount,
    linkHome,
    socials,
    textClose,
    textBack,
    search,
    mobileSearchText,
    push,
  } = props

  const [currentLevelOne, setCurrentLevelOne] = useState<number | null>(null)
  const [currentLevelTwo, setCurrentLevelTwo] = useState<number | null>(null)
  const [isSearchVisible, setIsSearchVisible] = useState(false)

  const searchInputRef = useRef<HTMLInputElement | null>(null)

  useEffect(() => {
    if (!searchInputRef?.current?.focus || isSearchVisible === false) {
      return
    }
    searchInputRef.current.focus()
  }, [isSearchVisible])

  const currentMenuLevel = useMemo(() => {
    if (currentLevelOne !== null && currentLevelTwo !== null) {
      return 2
    }
    if (currentLevelOne !== null) {
      return 1
    }
    return 0
  }, [currentLevelOne, currentLevelTwo])

  const toggleOpenHandler = useCallback(() => {
    // Reset menu
    setCurrentLevelOne(null)
    setCurrentLevelTwo(null)

    toggleMobileMenuOpen?.()
  }, [toggleMobileMenuOpen, setCurrentLevelOne, setCurrentLevelTwo])

  const renderLevelOne = useMemo(() => {
    return (
      <>
        <SC.Links>
          <SC.LinkItem>
            <SC.LinkText {...linkHome}>{linkHome?.text ?? ''}</SC.LinkText>
          </SC.LinkItem>
          {links &&
            links?.slice(0, links?.length - 1)?.map((item, index) => (
              <SC.LinkItem key={`nav-link-${item.link?.text}`}>
                <>
                  <SC.LinkText
                    {...(item?.levelTwo && item?.levelTwo?.length > 0
                      ? { onClickHandler: () => setCurrentLevelOne(index) }
                      : item.link)}
                  >
                    {item.link?.text ?? ''}
                  </SC.LinkText>
                  {item?.levelTwo && item?.levelTwo?.length > 0 && (
                    <SC.LinkCta onClick={() => setCurrentLevelOne(index)}>
                      <SC.CtaIcon icon="chevronRight" />
                    </SC.LinkCta>
                  )}
                </>
              </SC.LinkItem>
            ))}
        </SC.Links>
        <SC.Pushes>
          {push &&
            push?.map(
              (item, idx) =>
                item && (
                  <SC.Push
                    key={`nav-push-${idx}-${item?.firstHighlightText}`}
                    href={item?.ctaHref}
                  >
                    {item?.firstHighlightText}
                    <SC.CtaIcon icon="chevronRight" />
                  </SC.Push>
                )
            )}
        </SC.Pushes>
        <SC.Meta>
          <SC.YourAccount {...linkAccount}>
            <SC.YourAccountIcon icon="account" />
            <SC.YourAccountText>{linkAccount?.text ?? ''}</SC.YourAccountText>
          </SC.YourAccount>
          <SC.MetaDivider />
          <SC.Socials>
            {socials?.map((social) => (
              <SC.SocialButton key={social.icon} {...social} />
            ))}
          </SC.Socials>
        </SC.Meta>
      </>
    )
  }, [linkHome, links, push, linkAccount, socials])

  const renderLevelTwo = useMemo(() => {
    const levelOne = currentLevelOne !== null && links?.[currentLevelOne]
    return (
      <>
        <SC.Links $level={2}>
          <SC.BackButton
            text={textBack}
            icon="backArrow"
            variantType="button"
            variantColorScheme="white"
            actionProps={{ onClick: () => setCurrentLevelOne(null) }}
          />
          {levelOne && (
            <SC.LevelLinkItem>
              <SC.LevelText {...levelOne?.link}>
                {levelOne?.link?.text}
              </SC.LevelText>
            </SC.LevelLinkItem>
          )}
          {levelOne &&
            levelOne?.levelTwo?.map((item: any, index: any) => (
              <SC.LinkItem key={`nav-link-2-${item.link?.text}`}>
                <>
                  <SC.LinkText
                    {...(item?.levelThree && item?.levelThree?.length > 0
                      ? { onClickHandler: () => setCurrentLevelTwo(index) }
                      : item.link)}
                  >
                    {item.link?.text ?? ''}
                  </SC.LinkText>
                  {item?.levelThree && item?.levelThree?.length > 0 && (
                    <SC.LinkCta onClick={() => setCurrentLevelTwo(index)}>
                      <SC.CtaIcon icon="chevronRight" />
                    </SC.LinkCta>
                  )}
                </>
              </SC.LinkItem>
            ))}
        </SC.Links>
        {levelOne && levelOne?.push && (
          <SC.PushBlock>
            <NavPush {...levelOne?.push} />
          </SC.PushBlock>
        )}
      </>
    )
  }, [links, currentLevelOne, textBack])

  const renderLevelThree = useMemo(() => {
    const levelTwo =
      currentLevelOne !== null &&
      currentLevelTwo !== null &&
      links?.[currentLevelOne]?.levelTwo?.[currentLevelTwo]

    return (
      <SC.Links $level={3}>
        <SC.BackButton
          text={textBack}
          icon="backArrow"
          variantType="button"
          variantColorScheme="white"
          actionProps={{ onClick: () => setCurrentLevelTwo(null) }}
        />
        {levelTwo && (
          <SC.LevelLinkItem>
            <SC.LevelText {...levelTwo?.link}>
              {levelTwo?.link?.text}
            </SC.LevelText>
          </SC.LevelLinkItem>
        )}
        {levelTwo &&
          levelTwo?.levelThree?.map((item) => (
            <SC.LinkItem key={`nav-link-3-${item.link?.text}`}>
              <SC.LinkText {...item.link}>{item.link?.text ?? ''}</SC.LinkText>
            </SC.LinkItem>
          ))}
      </SC.Links>
    )
  }, [links, currentLevelOne, currentLevelTwo, textBack])

  return (
    <>
      <ScrollLock isActive={isOpen} />
      <TouchScrollable>
        <SC.Container className={className} isOpen={isOpen}>
          <SC.TopBar>
            <SC.ToggleSearch onClick={() => setIsSearchVisible(true)}>
              <SC.SearchPicto icon="search" />
              <SC.SearchText>{search.searchText}</SC.SearchText>
            </SC.ToggleSearch>
            <SC.SearchFormContainer isSearchVisible={isSearchVisible}>
              <SC.CloseSearchButton
                actionProps={{
                  onClick: () => setIsSearchVisible(false),
                }}
                text={textClose ?? ''}
                icon="close"
                variantType="button"
                variantColorScheme="iconBlack"
                variantColor="green"
                variantIcon="only"
              />
              <SC.SearchHeading>{mobileSearchText}</SC.SearchHeading>
              <SC.SearchForm
                {...search}
                onReady={(input) => (searchInputRef.current = input)}
              />
            </SC.SearchFormContainer>

            {/* <SC.Search type="search" placeholder={textSearch}></SC.Search> */}
            <SC.Close>
              <ActionButton
                actionProps={{
                  onClick: toggleOpenHandler,
                }}
                text={textClose ?? ''}
                icon="close"
                variantType="button"
                variantColorScheme="iconBlack"
                variantColor="green"
                variantIcon="only"
              />
            </SC.Close>
          </SC.TopBar>
          <SC.Menu currentMenuLevel={currentMenuLevel}>
            <SC.LevelOne>{renderLevelOne}</SC.LevelOne>
            <SC.LevelTwo>{renderLevelTwo}</SC.LevelTwo>
            <SC.LevelThree>{renderLevelThree}</SC.LevelThree>
          </SC.Menu>
        </SC.Container>
      </TouchScrollable>
    </>
  )
}

export default NavMobileMenu
